/**
 * iter733 — Extracted from SalesPage.jsx. Two related dialogs that
 * both close back into the sales list: ReceiveCreditDialog (booking
 * a partial-credit payment against an existing sale) and
 * DuplicateWarningDialog (asks the user to confirm before saving a
 * sale that has the same branch + customer + amount + date as an
 * existing one). All testids preserved.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle } from 'lucide-react';
import { cur } from '@/lib/currency';
import { DuplicatePeekList } from '@/components/common/DuplicatePeekList';

export function ReceiveCreditDialog({
  open,
  onOpenChange,
  receivingSale,
  receivePayment,
  setReceivePayment,
  branches = [],
  onSubmit,
}) {
  const getRemainingCredit = (sale) => (sale?.credit_amount || 0) - (sale?.credit_received || 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="receive-credit-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit">Receive Credit Payment</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground mb-2">
              Sale Amount: <span className="font-medium text-foreground"> {cur()} {receivingSale?.amount?.toFixed(2)}</span>
            </p>
            <p className="text-sm text-muted-foreground">
              Remaining Credit: <span className="font-bold text-credit"> {cur()} {getRemainingCredit(receivingSale || {}).toFixed(2)}</span>
            </p>
          </div>
          <div>
            <Label>Payment Amount *</Label>
            <Input
              type="number"
              step="0.01"
              value={receivePayment.amount}
              data-testid="receive-amount-input"
              onChange={(e) => setReceivePayment({ ...receivePayment, amount: e.target.value })}
              required
              max={getRemainingCredit(receivingSale || {})}
            />
          </div>
          <div>
            <Label>Payment Mode *</Label>
            <Select value={receivePayment.payment_mode} onValueChange={(val) => setReceivePayment({ ...receivePayment, payment_mode: val })}>
              <SelectTrigger data-testid="receive-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="bank">Bank</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Received By Branch</Label>
            <Select value={receivePayment.received_by_branch_id || receivingSale?.branch_id || 'owner'} onValueChange={(val) => setReceivePayment({ ...receivePayment, received_by_branch_id: val === 'owner' ? '' : val })}>
              <SelectTrigger data-testid="receive-branch-select">
                <SelectValue placeholder="Sale's branch" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="owner">Sale's branch (default)</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-3">
            <Button type="submit" data-testid="submit-receive-button" className="rounded-full">Receive Payment</Button>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function DuplicateWarningDialog({
  open,
  onOpenChange,
  duplicateCount,
  duplicateExisting,
  formData,
  amountTotal,
  onConfirm,
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="duplicate-warning-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-orange-600">
            <AlertTriangle size={20} /> Possible Duplicate Entry
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm">
            <span className="font-bold text-orange-700">{duplicateCount}</span> sale(s) with the same branch{formData.customer_id ? ', customer' : ' (walk-in)'} and amount
            (<span className="font-bold">{cur()} {amountTotal.toFixed(2)}</span>) already exist on{' '}
            <span className="font-bold">{formData.date}</span>.
            <br /><br />
            Are you sure this is <span className="font-bold">not a duplicate</span> entry?
          </AlertDialogDescription>
          <DuplicatePeekList
            rows={duplicateExisting}
            kind="sale"
            onItemClick={(id) => {
              onOpenChange(false);
              window.open(`/sales?focus=${encodeURIComponent(id)}`, '_blank', 'noopener');
            }}
          />
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="cancel-duplicate-btn">Cancel & Review</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-orange-600 hover:bg-orange-700" data-testid="confirm-duplicate-btn">
            Yes, Save Anyway
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
